<template>
  <div class="screenshot-ticket-fab">
    <!-- Shown only while collecting, between captures -->
    <v-btn
      v-if="collecting && !dialogOpen && !capturing"
      rounded
      small
      color="success"
      dark
      class="screenshot-ticket-fab-btn"
      @click="openForm"
    >
      <v-icon left small>{{ sendIcon }}</v-icon>
      {{ continueLabel }}
    </v-btn>

    <v-tooltip left>
      <template v-slot:activator="{ on }">
        <v-badge
          :value="screenshots.length > 0 && !dialogOpen"
          :content="screenshots.length"
          color="error"
          overlap
          offset-x="12"
          offset-y="12"
        >
          <v-btn
            :fab="!collecting"
            :rounded="collecting"
            small
            :color="collecting ? 'warning' : 'primary'"
            dark
            class="screenshot-ticket-fab-btn"
            v-on="on"
            @click="onFabClick"
            :loading="capturing"
            :disabled="captureLimitReached || dialogOpen"
          >
            <v-icon :left="collecting">{{ collecting ? cameraIcon : lifebuoyIcon }}</v-icon>
            <span v-if="collecting">{{ captureLabel }}</span>
          </v-btn>
        </v-badge>
      </template>
      <span v-if="captureLimitReached">
        Limit of {{ maxScreenshots }} screenshots reached — continue to the form
      </span>
      <span v-else-if="collecting">
        {{ screenshots.length }} collected so far
      </span>
      <span v-else>Request Support (Ctrl+Shift+S)</span>
    </v-tooltip>

    <ScreenshotTicketDialog
      v-model="dialogOpen"
      :screenshots="screenshots"
      :session-id="sessionId"
      :current-route="currentRouteName"
      :current-user="currentUser"
      :current-county="currentCounty"
      :workstation="workstation"
      @capture-more="startCollecting"
      @remove-screenshot="removeScreenshot"
    />
  </div>
</template>

<script>
import { mdiLifebuoy, mdiCamera, mdiSend } from '@mdi/js';
import ScreenshotService from './ScreenshotService';
import ScreenshotTicketDialog from './ScreenshotTicketDialog.vue';

// A support ticket carries at most this many screenshots. Each one is a
// full-viewport JPEG, so an uncapped collection can build a very large upload.
const MAX_SCREENSHOTS = 8;

export default {
  name: 'ScreenshotFab',
  components: { ScreenshotTicketDialog },
  data() {
    return {
      lifebuoyIcon: mdiLifebuoy,
      cameraIcon: mdiCamera,
      sendIcon: mdiSend,
      maxScreenshots: MAX_SCREENSHOTS,
      screenshots: [],
      dialogOpen: false,
      capturing: false,
      collecting: false,
      // Bumped when a brand-new ticket is started. The dialog watches this to
      // know when to clear the form — reopening it mid-flow (to collect more
      // screenshots) must NOT discard what the user already typed.
      sessionId: 0,
    };
  },
  computed: {
    // The full labels overflow a phone-width viewport, so shorten them there.
    continueLabel() {
      return this.$vuetify.breakpoint.xsOnly
        ? 'Continue to the form'
        : 'Continue to create the support ticket';
    },
    captureLabel() {
      return this.$vuetify.breakpoint.xsOnly ? 'Capture another' : 'Capture another screenshot';
    },
    captureLimitReached() {
      return this.screenshots.length >= MAX_SCREENSHOTS;
    },
    currentRouteName() {
      return this.$route ? this.$route.name || this.$route.path : '';
    },
    currentUser() {
      const state = this.$store && this.$store.state;
      const sc = state && state.SystemConfig;
      const item = sc && sc.workStationItem;
      return (item && item.userName) || '';
    },
    currentCounty() {
      const state = this.$store && this.$store.state;
      const sc = state && state.SystemConfig;
      const item = sc && sc.globalSettingItem;
      return (item && item.countyName) || '';
    },
    workstation() {
      const state = this.$store && this.$store.state;
      const sc = state && state.SystemConfig;
      const item = sc && sc.workStationItem;
      return (item && item.wkst) || '';
    },
  },
  watch: {
    dialogOpen(val) {
      if (!val && !this.collecting) {
        this.revokeAll();
        this.screenshots = [];
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown, { capture: true });
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown, { capture: true });
    this.revokeAll();
  },
  methods: {
    handleKeydown(e) {
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        e.stopPropagation();
        this.onFabClick();
      }
    },
    async onFabClick() {
      // dialogOpen guard: the keyboard shortcut can fire while the form is
      // open, and starting a new ticket there would wipe what the user typed.
      if (this.capturing || this.dialogOpen || this.captureLimitReached) return;

      // Not collecting means this click starts a fresh ticket.
      const startingNewTicket = !this.collecting;
      if (startingNewTicket) this.sessionId += 1;

      await this.captureAndAdd();

      if (startingNewTicket) this.dialogOpen = true;
    },
    async captureAndAdd() {
      this.capturing = true;
      try {
        const blob = await ScreenshotService.captureScreen();
        const previewUrl = URL.createObjectURL(blob);
        // Remember which screen this shot came from — the user can navigate
        // between captures, so the ticket's single "Page" is not enough.
        this.screenshots.push({ blob, previewUrl, route: this.currentRouteName });
      } catch (error) {
        console.error('Screenshot capture failed:', error);
        if (this.$store && this.$store.commit) {
          this.$store.commit('setSnackbar', {
            show: true,
            message: 'Screenshot capture failed. Please try again.',
            color: 'error',
          });
        }
      }
      this.capturing = false;
    },
    startCollecting() {
      this.collecting = true;
      this.dialogOpen = false;
    },
    openForm() {
      this.collecting = false;
      this.dialogOpen = true;
    },
    removeScreenshot(index) {
      const item = this.screenshots[index];
      if (item && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      this.screenshots.splice(index, 1);
      if (this.screenshots.length === 0) {
        this.collecting = false;
      }
    },
    revokeAll() {
      this.screenshots.forEach((s) => {
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      });
    },
  },
};
</script>

<style scoped>
/* One fixed stack for every floating control, so the buttons always share a
   right edge. z-index 210 clears the app bar (111, set in App.vue). The
   wrapper spans both buttons, so it must not eat clicks in the gap between
   them; pointer-events is inherited, so the buttons get it back through the
   tooltip/badge wrappers. */
.screenshot-ticket-fab {
  position: fixed;
  right: 16px;
  bottom: 120px;
  z-index: 210;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
}

.screenshot-ticket-fab > * {
  pointer-events: auto;
}

/* Sentence case reads better than Vuetify's default all-caps on labels this
   long, and these are read by users who are already having a bad day. */
.screenshot-ticket-fab-btn {
  text-transform: none;
  letter-spacing: normal;
}
</style>
